import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  ShoppingBag, 
  Pill, 
  Users, 
  FolderOpen, 
  UserCircle,
  BookOpen,
  Lock,
  Calendar,
  Plus,
  Edit,
  Trash2,
  Loader2,
  MapPin,
  Clock,
  UserCheck,
  MessageCircle,
  Video,
  TrendingUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPOS_EVENTO = [
  { value: 'general', label: 'General' },
  { value: 'quedada', label: 'Quedada' },
  { value: 'reto', label: 'Reto' },
  { value: 'taller', label: 'Taller' },
  { value: 'competicion', label: 'Competición' },
];

const AdminResourceCenterPage = () => {
  const [activeTab, setActiveTab] = useState('formacion');
  const [eventos, setEventos] = useState([]);
  const [loadingEventos, setLoadingEventos] = useState(true);
  const [showEventoDialog, setShowEventoDialog] = useState(false);
  const [editingEvento, setEditingEvento] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showParticipantes, setShowParticipantes] = useState(null);
  const [participantes, setParticipantes] = useState([]);
  
  const [eventoForm, setEventoForm] = useState({
    titulo: '',
    descripcion: '',
    fecha: '',
    hora: '',
    lugar: '',
    tipo: 'general',
    max_participantes: '',
    activo: true
  });

  useEffect(() => {
    fetchEventos();
  }, []);

  const fetchEventos = async () => {
    try {
      const response = await axios.get(`${API}/admin/eventos`);
      setEventos(response.data.eventos || []);
    } catch (error) {
      console.error('Error fetching eventos:', error);
    } finally {
      setLoadingEventos(false);
    }
  };

  const fetchParticipantes = async (eventoId) => {
    try {
      const response = await axios.get(`${API}/admin/eventos/${eventoId}/participantes`);
      setParticipantes(response.data.participantes || []);
      setShowParticipantes(eventoId);
    } catch (error) {
      toast.error('Error al cargar participantes');
    }
  };

  const resetForm = () => {
    setEventoForm({
      titulo: '',
      descripcion: '',
      fecha: '',
      hora: '',
      lugar: '',
      tipo: 'general',
      max_participantes: '',
      activo: true
    });
    setEditingEvento(null);
  };

  const openNewEvento = () => {
    resetForm();
    setShowEventoDialog(true);
  };

  const openEditEvento = (evento) => {
    setEditingEvento(evento);
    setEventoForm({
      titulo: evento.titulo,
      descripcion: evento.descripcion,
      fecha: evento.fecha,
      hora: evento.hora || '',
      lugar: evento.lugar || '',
      tipo: evento.tipo,
      max_participantes: evento.max_participantes?.toString() || '',
      activo: evento.activo
    });
    setShowEventoDialog(true);
  };

  const handleSubmitEvento = async () => {
    if (!eventoForm.titulo || !eventoForm.descripcion || !eventoForm.fecha) {
      toast.error('Completa título, descripción y fecha');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        ...eventoForm,
        max_participantes: eventoForm.max_participantes ? parseInt(eventoForm.max_participantes) : null
      };

      if (editingEvento) {
        await axios.put(`${API}/admin/eventos/${editingEvento.id}`, data);
        toast.success('Evento actualizado');
      } else {
        await axios.post(`${API}/admin/eventos`, data);
        toast.success('Evento creado');
      }
      
      setShowEventoDialog(false);
      resetForm();
      fetchEventos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvento = async (eventoId) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    
    try {
      await axios.delete(`${API}/admin/eventos/${eventoId}`);
      toast.success('Evento eliminado');
      fetchEventos();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getTipoColor = (tipo) => {
    switch (tipo) {
      case 'quedada': return 'bg-blue-500/20 text-blue-400';
      case 'reto': return 'bg-orange-500/20 text-orange-400';
      case 'taller': return 'bg-green-500/20 text-green-400';
      case 'competicion': return 'bg-red-500/20 text-red-400';
      default: return 'bg-purple-500/20 text-purple-400';
    }
  };

  const formatFecha = (fecha) => {
    const date = new Date(fecha + 'T00:00:00');
    return date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in" data-testid="admin-resource-center">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <BookOpen className="h-7 w-7 text-primary" />
              Centro de Recursos
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestiona los recursos y contenidos para tus clientes
            </p>
          </div>
        </div>

        {/* Tabs - 6 pestañas */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-6 gap-1 bg-card border border-border p-1 h-auto">
            <TabsTrigger 
              value="formacion"
              className="flex items-center gap-2 data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400 text-xs px-2"
            >
              <GraduationCap className="h-4 w-4" />
              <span className="hidden sm:inline">Formación</span>
            </TabsTrigger>
            <TabsTrigger 
              value="equipamiento"
              className="flex items-center gap-2 data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400 text-xs px-2"
            >
              <ShoppingBag className="h-4 w-4" />
              <span className="hidden sm:inline">Textil</span>
            </TabsTrigger>
            <TabsTrigger 
              value="suplementacion"
              className="flex items-center gap-2 data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400 text-xs px-2"
            >
              <Pill className="h-4 w-4" />
              <span className="hidden sm:inline">Suplem.</span>
            </TabsTrigger>
            <TabsTrigger 
              value="comunidad"
              className="flex items-center gap-2 data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400 text-xs px-2"
            >
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Comunidad</span>
            </TabsTrigger>
            <TabsTrigger 
              value="recursos"
              className="flex items-center gap-2 data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400 text-xs px-2"
            >
              <FolderOpen className="h-4 w-4" />
              <span className="hidden sm:inline">Recursos</span>
            </TabsTrigger>
            <TabsTrigger 
              value="estadisticas"
              className="flex items-center gap-2 data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 text-xs px-2"
            >
              <TrendingUp className="h-4 w-4" />
              <span className="hidden sm:inline">Stats</span>
            </TabsTrigger>
          </TabsList>

          {/* FORMACIÓN */}
          <TabsContent value="formacion" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <GraduationCap className="h-5 w-5 text-blue-400" />
                  Formación
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Contenido formativo para tus clientes. Gestiona desde el menú "Formación" en el panel lateral.
                </p>
                
                <div className="grid gap-3">
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Video className="h-8 w-8 text-blue-400" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">Videoteca</h4>
                        <p className="text-xs text-muted-foreground">Videos formativos para clientes</p>
                      </div>
                      <Button variant="outline" size="sm" disabled>
                        <Lock className="h-4 w-4 mr-1" />
                        Próximamente
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <BookOpen className="h-8 w-8 text-blue-400" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">Píldoras Progresivas</h4>
                        <p className="text-xs text-muted-foreground">Ve a Formación en el menú lateral</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => window.location.href = '/admin/pildoras'}>
                        Gestionar
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* EQUIPAMIENTO / TEXTIL */}
          <TabsContent value="equipamiento" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ShoppingBag className="h-5 w-5 text-amber-400" />
                  Tienda GuardiansFit - Textil
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Productos de merchandising que verán tus clientes.
                </p>
                
                {/* Productos actuales */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Camiseta */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg overflow-hidden">
                    <div className="aspect-square bg-zinc-900 flex items-center justify-center p-2">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_43333703-e2fe-4e22-b621-2a9a0890a0f7/artifacts/hulpxpa5_6F879F4F-7B18-4BEC-947F-99F17FEC45ED%20%281%29.jpeg"
                        alt="Camiseta"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="font-semibold text-foreground text-sm">Camiseta Técnica</h4>
                      <p className="text-amber-400 font-bold">15€</p>
                    </div>
                  </div>

                  {/* Polo */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg overflow-hidden">
                    <div className="aspect-square bg-zinc-900 flex items-center justify-center p-2">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_43333703-e2fe-4e22-b621-2a9a0890a0f7/artifacts/0cvgg75o_C207C8B5-0C43-4FAD-96A0-43587A319DD9.jpeg"
                        alt="Polo"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="font-semibold text-foreground text-sm">Polo Oficial</h4>
                      <p className="text-amber-400 font-bold">25€</p>
                    </div>
                  </div>

                  {/* Botella */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg overflow-hidden">
                    <div className="aspect-square bg-zinc-900 flex items-center justify-center p-2">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_43333703-e2fe-4e22-b621-2a9a0890a0f7/artifacts/f7jo83s6_88D8C4A8-CB1E-4D0F-ACE2-AFA32FFA6513.jpeg"
                        alt="Botella"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="font-semibold text-foreground text-sm">Botella 2.2L</h4>
                      <p className="text-amber-400 font-bold">15€</p>
                    </div>
                  </div>

                  {/* Toalla */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg overflow-hidden">
                    <div className="aspect-square bg-zinc-900 flex items-center justify-center p-2">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_43333703-e2fe-4e22-b621-2a9a0890a0f7/artifacts/9uijt78o_99512F24-1AB8-4306-9E58-79A3B3D2BAD5.jpeg"
                        alt="Toalla"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="font-semibold text-foreground text-sm">Toalla Gym</h4>
                      <p className="text-amber-400 font-bold">10€</p>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-center text-muted-foreground pt-4 border-t border-zinc-700">
                  Los clientes verán estos productos en su Centro de Recursos → Equipamiento
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SUPLEMENTACIÓN */}
          <TabsContent value="suplementacion" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Pill className="h-5 w-5 text-green-400" />
                  Suplementación
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Recomendaciones de suplementos para tus clientes.
                </p>
                
                <div className="p-6 bg-zinc-800/30 border border-dashed border-zinc-700 rounded-lg text-center">
                  <Pill className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Próximamente</p>
                  <p className="text-xs text-muted-foreground mt-1">Podrás añadir recomendaciones de suplementos con enlaces de afiliado</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* COMUNIDAD - Con Eventos */}
          <TabsContent value="comunidad" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Users className="h-5 w-5 text-purple-400" />
                    Comunidad - Eventos
                  </CardTitle>
                  <Button onClick={openNewEvento} className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Nuevo Evento
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Crea eventos para informar a tus clientes de actividades.
                </p>

                {loadingEventos ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
                  </div>
                ) : eventos.length === 0 ? (
                  <div className="p-8 bg-zinc-800/50 rounded-lg text-center">
                    <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No hay eventos creados</p>
                    <p className="text-xs text-muted-foreground mt-1">Crea tu primer evento para informar a tus clientes</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {eventos.map((evento) => (
                      <div 
                        key={evento.id}
                        className={`p-4 rounded-lg border ${evento.activo ? 'bg-zinc-800/50 border-zinc-700' : 'bg-zinc-900/50 border-zinc-800 opacity-60'}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTipoColor(evento.tipo)}`}>
                                {TIPOS_EVENTO.find(t => t.value === evento.tipo)?.label}
                              </span>
                              {!evento.activo && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-zinc-700 text-zinc-400">
                                  Inactivo
                                </span>
                              )}
                            </div>
                            
                            <h4 className="font-semibold text-foreground">{evento.titulo}</h4>
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{evento.descripcion}</p>
                            
                            <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatFecha(evento.fecha)}
                              </span>
                              {evento.hora && (
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {evento.hora}h
                                </span>
                              )}
                              {evento.lugar && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  {evento.lugar}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {evento.participantes?.length || 0} apuntados
                                {evento.max_participantes && ` / ${evento.max_participantes}`}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => fetchParticipantes(evento.id)}
                              className="text-purple-400 hover:bg-purple-500/20"
                              title="Ver participantes"
                            >
                              <UserCheck className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditEvento(evento)}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteEvento(evento.id)}
                              className="text-destructive hover:bg-destructive/20"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* RECURSOS */}
          <TabsContent value="recursos" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FolderOpen className="h-5 w-5 text-orange-400" />
                  Recursos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-6 bg-zinc-800/30 border border-dashed border-zinc-700 rounded-lg text-center">
                  <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Próximamente</p>
                  <p className="text-xs text-muted-foreground mt-1">Podrás subir PDFs, documentos y recursos para tus clientes</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ESTADÍSTICAS */}
          <TabsContent value="estadisticas" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="h-5 w-5 text-cyan-400" />
                  Estadísticas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-6 bg-zinc-800/30 border border-dashed border-zinc-700 rounded-lg text-center">
                  <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Próximamente</p>
                  <p className="text-xs text-muted-foreground mt-1">Estadísticas de uso del Centro de Recursos</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialog crear/editar evento */}
        <Dialog open={showEventoDialog} onOpenChange={setShowEventoDialog}>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-purple-400" />
                {editingEvento ? 'Editar Evento' : 'Nuevo Evento'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  value={eventoForm.titulo}
                  onChange={(e) => setEventoForm({ ...eventoForm, titulo: e.target.value })}
                  placeholder="Ej: Quedada mensual en el parque"
                  className="bg-zinc-900/50 border-zinc-800"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Descripción *</Label>
                <Textarea
                  value={eventoForm.descripcion}
                  onChange={(e) => setEventoForm({ ...eventoForm, descripcion: e.target.value })}
                  placeholder="Describe el evento..."
                  className="bg-zinc-900/50 border-zinc-800 min-h-[80px]"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha *</Label>
                  <Input
                    type="date"
                    value={eventoForm.fecha}
                    onChange={(e) => setEventoForm({ ...eventoForm, fecha: e.target.value })}
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hora</Label>
                  <Input
                    type="time"
                    value={eventoForm.hora}
                    onChange={(e) => setEventoForm({ ...eventoForm, hora: e.target.value })}
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select
                    value={eventoForm.tipo}
                    onValueChange={(value) => setEventoForm({ ...eventoForm, tipo: value })}
                  >
                    <SelectTrigger className="bg-zinc-900/50 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_EVENTO.map((tipo) => (
                        <SelectItem key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Plazas máximas</Label>
                  <Input
                    type="number"
                    min="1"
                    value={eventoForm.max_participantes}
                    onChange={(e) => setEventoForm({ ...eventoForm, max_participantes: e.target.value })}
                    placeholder="Sin límite"
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Lugar</Label>
                <Input
                  value={eventoForm.lugar}
                  onChange={(e) => setEventoForm({ ...eventoForm, lugar: e.target.value })}
                  placeholder="Ej: Parque del Retiro, entrada principal"
                  className="bg-zinc-900/50 border-zinc-800"
                />
              </div>
            </div>
            
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setShowEventoDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSubmitEvento}
                disabled={submitting}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {editingEvento ? 'Guardar' : 'Crear Evento'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog ver participantes */}
        <Dialog open={!!showParticipantes} onOpenChange={() => setShowParticipantes(null)}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-purple-400" />
                Participantes ({participantes.length})
              </DialogTitle>
            </DialogHeader>
            
            <div className="mt-4">
              {participantes.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Nadie se ha apuntado todavía
                </p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {participantes.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                        <UserCircle className="h-5 w-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{p.name}</p>
                        <p className="text-xs text-muted-foreground">{p.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminResourceCenterPage;
